import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Activity,
  CheckCircle2,
  Copy,
  ExternalLink,
  EyeOff,
  FileBarChart,
  Globe,
  Image as ImageIcon,
  Link as LinkIcon,
  Mail,
  Palette,
  Plus,
  RefreshCw,
  Save,
  Sparkles,
  Trash2,
  Type,
} from 'lucide-react';
import { api } from '../../api/client';
import EmptyState from '../../components/EmptyState';
import InfoTip from '../../components/InfoTip';
import ErrorBanner from '../../components/ErrorBanner';
import { SkeletonList } from '../../components/Skeleton';
import { toast } from '../../lib/toast';
import { formatDateTime, formatRelative } from '../../lib/format';
import { useFeature, upgradeHintFor } from '../../lib/features';
import { FileDown, Loader2, Lock, RotateCw } from 'lucide-react';

// When a request uses responseType: 'blob', an error response body arrives as
// a Blob too — so err.response.data.error is undefined and the toast falls back
// to axios's generic "Request failed with status code 500". This reads the blob
// back as JSON to recover the real server message.
async function messageFromBlobError(err, fallback) {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const json = JSON.parse(text);
      if (json?.error) return json.error;
    } catch {
      // Not JSON (e.g. an HTML 502 page) — fall through to the fallback.
    }
  }
  return err?.response?.data?.error || err?.message || fallback;
}

export default function ReportsTab({ domain }) {
  const qc = useQueryClient();
  const [downloadingId, setDownloadingId] = useState(null);

  const brandQuery = useQuery({
    queryKey: ['brand-profile'],
    queryFn: () => api.get('/api/me/brand-profile').then((r) => r.data.profile),
  });

  const reportsQuery = useQuery({
    queryKey: ['reports', domain._id],
    queryFn: () =>
      api.get(`/api/domains/${domain._id}/reports`).then((r) => r.data.reports),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      api.post(`/api/domains/${domain._id}/reports`).then((r) => r.data.report),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reports', domain._id] });
      toast.success('Report generated');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/domains/${domain._id}/reports/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reports', domain._id] });
      toast.success('Report deleted');
    },
    onError: (err) => toast.error(err, 'Could not delete report'),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, rotate }) =>
      api
        .post(`/api/domains/${domain._id}/reports/${id}/visibility`, { rotate })
        .then((r) => r.data.report),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['reports', domain._id] });
      toast.success(vars?.rotate ? 'Link rotated' : 'Visibility updated');
    },
    onError: (err) => toast.error(err, 'Could not update visibility'),
  });

  // Share-link triad — explicit endpoints (Phase 4 Slice H).
  const rotateSlugMutation = useMutation({
    mutationFn: (id) =>
      api.post(`/api/domains/${domain._id}/reports/${id}/rotate-slug`).then((r) => r.data.report),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reports', domain._id] });
      toast.success('Share link rotated — the old URL no longer works');
    },
    onError: (err) => toast.error(err, 'Could not rotate link'),
  });
  const regenerateMutation = useMutation({
    mutationFn: (id) =>
      api.post(`/api/domains/${domain._id}/reports/${id}/regenerate`).then((r) => r.data.report),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reports', domain._id] });
      toast.success('Report refreshed with latest data');
    },
    onError: (err) => toast.error(err, 'Could not regenerate'),
  });
  const revokeMutation = useMutation({
    mutationFn: (id) =>
      api.post(`/api/domains/${domain._id}/reports/${id}/revoke-share`).then((r) => r.data.report),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reports', domain._id] });
      toast.success('Share link revoked');
    },
    onError: (err) => toast.error(err, 'Could not revoke share'),
  });

  // White-label PDF (Agency / Enterprise). Server-side Puppeteer render can
  // take several seconds, so we surface a per-row spinner via downloadingId.
  async function downloadPdf(id, title) {
    setDownloadingId(id);
    try {
      const res = await api.get(`/api/domains/${domain._id}/reports/${id}/pdf`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(title || 'report').replace(/[^a-z0-9\-_]+/gi, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
    } catch (err) {
      toast.error(await messageFromBlobError(err, 'PDF generation failed'));
    } finally {
      setDownloadingId(null);
    }
  }

  const reports = reportsQuery.data || [];

  return (
    <div className="space-y-6">
      <BrandHero
        brand={brandQuery.data}
        loading={brandQuery.isLoading}
        onGenerate={() => createMutation.mutate()}
        generating={createMutation.isPending}
      />

      {createMutation.isError && (
        <ErrorBanner
          title="Could not generate report"
          body={createMutation.error?.response?.data?.error || 'Try again, or check that your audit/analytics data exists for this domain.'}
          onDismiss={() => createMutation.reset()}
        />
      )}

      <BrandProfileEditor brand={brandQuery.data} />

      {reportsQuery.isLoading ? (
        <SkeletonList rows={3} />
      ) : !reports.length ? (
        <EmptyState
          icon={FileBarChart}
          title="No reports yet"
          body="Generate a snapshot report that bundles the latest audit, content gaps, and 30-day analytics into one branded document — perfect for client deliverables."
          action={
            <button
              className="btn-primary"
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? (
                <>
                  <Activity className="w-4 h-4 animate-pulse" /> Generating…
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" /> Generate first report
                </>
              )}
            </button>
          }
        />
      ) : (
        <ReportsList
          reports={reports}
          onDelete={(id) => {
            if (window.confirm('Delete this report? The share link will stop working.')) {
              deleteMutation.mutate(id);
            }
          }}
          onToggleVisibility={(id) => toggleMutation.mutate({ id })}
          onRotateLink={(id) => rotateSlugMutation.mutate(id)}
          onRegenerate={(id) => regenerateMutation.mutate(id)}
          onRevoke={(id) => revokeMutation.mutate(id)}
          onDownloadPdf={(id, title) => downloadPdf(id, title)}
          downloadingId={downloadingId}
        />
      )}
    </div>
  );
}

function BrandHero({ brand, loading, onGenerate, generating }) {
  const agency = brand?.agencyName || 'Your agency';
  return (
    <div className="relative rounded-2xl overflow-hidden bg-ink-gradient text-white p-6 sm:p-8 shadow-xl">
      <div className="absolute inset-0 opacity-30 bg-brand-radial" />
      <div className="absolute inset-0 bg-grid opacity-20" />
      <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-brand-600 blur-3xl opacity-30" />
      <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-white/60 mb-1 flex items-center gap-1.5">
            <FileBarChart className="w-3 h-3" /> Client reports
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold inline-flex items-center gap-2">
            White-label snapshots, one click away
            <span className="text-white/70">
              <InfoTip
                title="What's in a report?"
                body="Each report bundles the latest GEO audit, content gaps, AI traffic, and bot activity for the selected domain into a single shareable page. The link is unguessable; toggle visibility or rotate the slug anytime."
              />
            </span>
          </h2>
          <p className="text-sm text-white/70 mt-1 max-w-xl">
            Branded, shareable, and renderable as PDF.{' '}
            {loading ? null : <>Reports go out as <strong>{agency}</strong>.</>}
          </p>
        </div>
        <button
          onClick={onGenerate}
          disabled={generating}
          className="inline-flex items-center gap-2 rounded-lg bg-white text-ink-900 px-4 py-2 text-sm font-semibold hover:bg-white/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {generating ? (
            <>
              <Activity className="w-4 h-4 animate-pulse" /> Generating…
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" /> Generate report
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function BrandProfileEditor({ brand }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (brand && !form) {
      setForm({
        agencyName: brand.agencyName || '',
        tagline: brand.tagline || '',
        logoUrl: brand.logoUrl || '',
        primaryColor: brand.primaryColor || '#d61f97',
        accentColor: brand.accentColor || '#ffa23d',
        contactEmail: brand.contactEmail || '',
        contactUrl: brand.contactUrl || '',
        footerNote: brand.footerNote || '',
      });
    }
  }, [brand, form]);

  const updateMutation = useMutation({
    mutationFn: (body) => api.put('/api/me/brand-profile', body).then((r) => r.data.profile),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['brand-profile'] });
      toast.success('Branding saved');
    },
    onError: (err) => toast.error(err, 'Could not save branding'),
  });

  if (!form) {
    return <SkeletonList rows={4} />;
  }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <Palette className="w-4 h-4" />
          </span>
          Brand profile
        </div>
        <span className="text-xs text-ink-400">Used in all future reports</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="label">Agency name</label>
          <div className="relative">
            <Type className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
            <input
              className="input pl-10"
              value={form.agencyName}
              onChange={set('agencyName')}
              placeholder="Acme Marketing Co."
            />
          </div>
        </div>
        <div>
          <label className="label">Tagline (optional)</label>
          <input
            className="input"
            value={form.tagline}
            onChange={set('tagline')}
            placeholder="AI Search visibility, on autopilot"
          />
        </div>
        <div className="md:col-span-2">
          <label className="label">Logo URL</label>
          <div className="relative">
            <ImageIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
            <input
              className="input pl-10"
              value={form.logoUrl}
              onChange={set('logoUrl')}
              placeholder="https://your-cdn.com/logo.png"
            />
          </div>
          <p className="text-xs text-ink-400 mt-1.5">
            Host your logo anywhere reachable (Cloudinary, S3, your own site) and paste the URL
            here. ~32-48px tall PNGs work best.
          </p>
          {form.logoUrl && (
            <div className="mt-2 rounded-lg bg-gray-50 border border-gray-100 p-3 inline-flex items-center gap-2">
              <span className="text-xs text-ink-400">Preview:</span>
              <img
                src={form.logoUrl}
                alt="Logo preview"
                className="h-8 w-auto"
                onError={(e) => (e.target.style.display = 'none')}
              />
            </div>
          )}
        </div>
        <div>
          <label className="label">Primary color</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={form.primaryColor}
              onChange={set('primaryColor')}
              className="h-10 w-12 rounded-lg border border-gray-200 cursor-pointer"
            />
            <input
              className="input flex-1 font-mono"
              value={form.primaryColor}
              onChange={set('primaryColor')}
              placeholder="#d61f97"
            />
          </div>
        </div>
        <div>
          <label className="label">Accent color</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={form.accentColor}
              onChange={set('accentColor')}
              className="h-10 w-12 rounded-lg border border-gray-200 cursor-pointer"
            />
            <input
              className="input flex-1 font-mono"
              value={form.accentColor}
              onChange={set('accentColor')}
              placeholder="#ffa23d"
            />
          </div>
        </div>
        <div>
          <label className="label">Contact email</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
            <input
              className="input pl-10"
              type="email"
              value={form.contactEmail}
              onChange={set('contactEmail')}
              placeholder="hello@acme.com"
            />
          </div>
        </div>
        <div>
          <label className="label">Contact URL</label>
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
            <input
              className="input pl-10"
              type="url"
              value={form.contactUrl}
              onChange={set('contactUrl')}
              placeholder="https://acme.com"
            />
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="label">Footer note (optional)</label>
          <textarea
            className="input min-h-[64px]"
            value={form.footerNote}
            onChange={set('footerNote')}
            placeholder="Generated by Acme Marketing Co. · Confidential — for client use only"
          />
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <button
          className="btn-primary"
          onClick={() => updateMutation.mutate(form)}
          disabled={updateMutation.isPending}
        >
          <Save className="w-4 h-4" />
          {updateMutation.isPending ? 'Saving…' : 'Save branding'}
        </button>
      </div>
    </div>
  );
}

function ReportsList({ reports, onDelete, onToggleVisibility, onRotateLink, onRegenerate, onRevoke, onDownloadPdf, downloadingId }) {
  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <FileBarChart className="w-4 h-4" />
          </span>
          Reports
        </div>
        <span className="text-xs text-ink-400">
          {reports.length} {reports.length === 1 ? 'report' : 'reports'}
        </span>
      </div>
      <ul className="divide-y divide-gray-50">
        {reports.map((r) => (
          <ReportRow
            key={r._id}
            report={r}
            onDelete={onDelete}
            onToggleVisibility={onToggleVisibility}
            onRotateLink={onRotateLink}
            onRegenerate={onRegenerate}
            onRevoke={onRevoke}
            onDownloadPdf={onDownloadPdf}
            downloading={downloadingId === r._id}
          />
        ))}
      </ul>
    </div>
  );
}

function ReportRow({ report, onDelete, onToggleVisibility, onRotateLink, onRegenerate, onRevoke, onDownloadPdf, downloading }) {
  const hasWhiteLabel = useFeature('whiteLabelReports');
  const [copied, setCopied] = useState(false);
  const publicUrl = `${window.location.origin}/r/${report.slug}`;
  const score = report.data?.audit?.avg_score;

  const onCopy = async () => {
    await navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <li className="py-3 flex items-center justify-between gap-3 first:pt-0 last:pb-0">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <span
          className={`flex-none h-10 w-10 rounded-xl flex items-center justify-center font-bold text-sm ${
            score == null
              ? 'bg-gray-100 text-ink-700'
              : score >= 75
                ? 'bg-emerald-50 text-emerald-700'
                : score >= 50
                  ? 'bg-amber-50 text-amber-700'
                  : 'bg-rose-50 text-rose-600'
          }`}
        >
          {score != null ? score : '—'}
        </span>
        <div className="min-w-0">
          <div className="font-medium text-ink-900 truncate">{report.title}</div>
          <div className="text-xs text-ink-400 truncate">
            {formatDateTime(report.createdAt)} · updated {formatRelative(report.updatedAt)}
            {!report.is_public && ' · private'}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1 flex-none">
        <Link to={`/report/${report._id}`} className="btn-icon" title="Open report">
          <ExternalLink className="w-4 h-4" />
        </Link>
        {report.is_public && (
          <button className="btn-icon" title="Copy share link" onClick={onCopy}>
            {copied ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            ) : (
              <LinkIcon className="w-4 h-4" />
            )}
          </button>
        )}
        <button
          className="btn-icon"
          title={report.is_public ? 'Make private' : 'Make public'}
          onClick={() => onToggleVisibility(report._id)}
        >
          {report.is_public ? <EyeOff className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
        </button>
        {report.is_public && (
          <button
            className="btn-icon"
            title="Rotate share link (old link stops working)"
            onClick={() => {
              if (window.confirm("Rotate this report's share link? The old link will stop working.")) {
                onRotateLink(report._id);
              }
            }}
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        )}
        <button
          className="btn-icon"
          title="Regenerate snapshot (re-fetch latest audit + analytics)"
          onClick={() => onRegenerate(report._id)}
        >
          <RotateCw className="w-4 h-4" />
        </button>
        {report.is_public && (
          <button
            className={hasWhiteLabel ? 'btn-icon' : 'btn-icon opacity-60 cursor-not-allowed'}
            disabled={downloading}
            title={
              hasWhiteLabel
                ? downloading
                  ? 'Generating PDF…'
                  : 'Download white-label PDF'
                : `White-label PDF requires ${upgradeHintFor('whiteLabelReports')}`
            }
            onClick={() => {
              if (downloading) return;
              if (!hasWhiteLabel) {
                toast.error(`White-label PDF is an ${upgradeHintFor('whiteLabelReports')} feature.`);
                return;
              }
              onDownloadPdf(report._id, report.title);
            }}
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : hasWhiteLabel ? (
              <FileDown className="w-4 h-4" />
            ) : (
              <Lock className="w-4 h-4" />
            )}
          </button>
        )}
        {report.is_public && (
          <button
            className="btn-icon hover:!text-amber-600 hover:!bg-amber-50"
            title="Revoke share — set this report private"
            onClick={() => {
              if (window.confirm('Revoke the public share link? The URL will stop working immediately.')) {
                onRevoke(report._id);
              }
            }}
          >
            <EyeOff className="w-4 h-4" />
          </button>
        )}
        <button
          className="btn-icon hover:!text-rose-600 hover:!bg-rose-50"
          title="Delete report"
          onClick={() => onDelete(report._id)}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </li>
  );
}
